"use client";

import type { FC } from "react";

import { SelectionTileItem } from "@jti/core-components";
import { Expandable } from "@jti/ui/components/Expandable";
import { StrengthIndicator } from "@jti/ui/components/StrengthIndicator";

import type { FilterSection } from "@commerce/features";

import styles from "./AttributeFilterSections.module.scss";

type SectionBodyProps = {
  section: FilterSection;
  groupName: string;
  onToggleOption: (sectionId: string, value: string) => void;
};

const SectionBody: FC<SectionBodyProps> = ({ section, groupName, onToggleOption }) => {
  if (section.strengthGroups) {
    return (
      <div className={styles.strengthGroups}>
        {section.strengthGroups.map((group) => (
          <div key={group.levelLabel} className={styles.strengthGroup}>
            <span className={styles.strengthGroupLabel}>{group.levelLabel}</span>
            <div className={styles.strengthOptions}>
              {group.options.map((option) => (
                <div key={option.value} data-testid={`attribute-filter-option-${option.value}`}>
                  <SelectionTileItem
                    type="checkbox"
                    name={groupName}
                    value={option.value}
                    label={<StrengthIndicator name={option.label} strength={option.rating ?? 0} />}
                    checked={option.selected}
                    disabled={option.disabled}
                    onChange={() => onToggleOption(section.id, option.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.options}>
      {section.options.map((option) => (
        <div key={option.value} data-testid={`attribute-filter-option-${option.value}`}>
          <SelectionTileItem
            type="checkbox"
            name={groupName}
            value={option.value}
            label={option.label}
            checked={option.selected}
            disabled={option.disabled}
            onChange={() => onToggleOption(section.id, option.value)}
          />
        </div>
      ))}
    </div>
  );
};

export type AttributeFilterSectionsProps = {
  sections: FilterSection[];
  expandedSectionId?: string;
  onToggleOption: (sectionId: string, value: string) => void;
};

export const AttributeFilterSections: FC<AttributeFilterSectionsProps> = ({
  sections,
  expandedSectionId,
  onToggleOption,
}) => {
  return (
    <>
      {sections.map((section) => (
        <Expandable key={section.id} label={section.label} defaultExpanded={section.id === expandedSectionId}>
          <SectionBody section={section} groupName={section.id} onToggleOption={onToggleOption} />
        </Expandable>
      ))}
    </>
  );
};
